/**
 * Simplificação do FP&A (ADR 0049), PR-2: Catálogo de Gastos, linhas gerenciais de P&L (XX.XX) e
 * conferência técnica do De-Para. As regras de negócio são conferidas de novo no banco.
 */
import { z } from 'zod';
import { localizedText } from './schemas.ts';

const uuid = z.string().uuid();
const notes = z
  .string()
  .trim()
  .max(1000)
  .nullish()
  .transform((v) => (v ? v : null));
const sortOrder = z.number().int().min(1).max(9999).default(100);
const catalogCode = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9][A-Z0-9_-]{1,39}$/, 'Use de 2 a 40 letras maiúsculas, números, - ou _');
/** Descrição nos dois idiomas, opcional: vazia nos dois = sem descrição. */
const localizedDescription = z
  .object({ 'pt-BR': z.string().trim().max(1000), en: z.string().trim().max(1000) })
  .nullish()
  .transform((v) => (v && (v['pt-BR'] || v.en) ? v : null));

export const FPA_PL_BLOCKS = ['result', 'outside'] as const;
/**
 * Natureza gerencial da seção no mapeamento (PR-8e): decide em qual botão o Tipo de Gasto aparece
 * (Nova receita: receita e dedução; Novo custo: custo; Nova despesa: despesa e investimento).
 * Vazia = pendente de validação (o Tipo de Gasto aparece à parte, com aviso).
 */
export const FPA_PL_NATURES = ['revenue', 'deduction', 'cost', 'expense', 'investment'] as const;
export type FpaPlNature = (typeof FPA_PL_NATURES)[number];
export type FpaPlBlock = (typeof FPA_PL_BLOCKS)[number];

/** Código de dois dígitos da seção (Tipo de Gasto), independente do ID do NetSuite. */
export const fpaSectionCode = z
  .string()
  .trim()
  .regex(/^[0-9]{2}$/, 'Use dois dígitos (ex.: 04)');
/** Código XX.XX da linha gerencial (seção + Tipo de Classificação). */
export const fpaLineCode = z
  .string()
  .trim()
  .regex(/^[0-9]{2}\.[0-9]{2}$/, 'Use o formato XX.XX (ex.: 04.28)');

/** O código da linha começa com o código da seção? */
export const lineCodeMatchesSection = (lineCode: string, sectionCode: string) =>
  lineCode.slice(0, 2) === sectionCode;

export const fpaPlSectionInput = z.object({
  mappingVersionId: uuid,
  expenseTypeId: uuid,
  code: fpaSectionCode,
  block: z.enum(FPA_PL_BLOCKS),
  isSubtotal: z.boolean().default(false),
  /** Sinal nos subtotais da DRE (PR-6): 1 soma, -1 subtrai; vazio = a definir. */
  sign: z
    .union([z.literal(1), z.literal(-1)])
    .nullish()
    .transform((v) => v ?? null),
  nature: z
    .enum(FPA_PL_NATURES)
    .nullish()
    .transform((v) => v ?? null),
  sortOrder,
  notes,
});
export type FpaPlSectionInput = z.infer<typeof fpaPlSectionInput>;
export const fpaPlSectionUpdateInput = fpaPlSectionInput.omit({
  mappingVersionId: true,
  expenseTypeId: true,
});

/**
 * Sinal próprio da linha (0104): 1 soma, -1 subtrai, 0 = os dois sentidos (o valor lançado já traz
 * o sinal: ganho positivo, perda negativa); vazio = o sinal da seção.
 */
const lineSign = z
  .union([z.literal(1), z.literal(-1), z.literal(0)])
  .nullish()
  .transform((v) => v ?? null);

export const fpaPlLineInput = z.object({
  mappingVersionId: uuid,
  sectionId: uuid,
  classificationId: uuid,
  code: fpaLineCode,
  sign: lineSign,
  sortOrder,
  notes,
});
export type FpaPlLineInput = z.infer<typeof fpaPlLineInput>;
export const fpaPlLineUpdateInput = z.object({
  code: fpaLineCode,
  sign: lineSign,
  sortOrder,
  notes,
  isActive: z.boolean().default(true),
});

export const fpaSpendGroupInput = z.object({
  code: catalogCode,
  name: localizedText,
  description: localizedDescription,
  sortOrder,
});
export type FpaSpendGroupInput = z.infer<typeof fpaSpendGroupInput>;

/** Tipo do item do Catálogo (PR-8b): a porta de entrada do lançamento, não o sinal na DRE. */
export const FPA_SPEND_KINDS = ['revenue', 'cost', 'expense'] as const;
export type FpaSpendKind = (typeof FPA_SPEND_KINDS)[number];

export const fpaSpendItemInput = z.object({
  groupId: uuid,
  code: catalogCode,
  name: localizedText,
  description: localizedDescription,
  expenseTypeId: uuid,
  classificationId: uuid,
  accountingAccountId: uuid.nullish().transform((v) => v ?? null),
  /** Sem o tipo: o item novo fica como despesa e o existente mantém o que tinha. */
  kind: z.enum(FPA_SPEND_KINDS).optional(),
});
export type FpaSpendItemInput = z.infer<typeof fpaSpendItemInput>;

export const fpaSpendItemRequestInput = z.object({
  groupId: uuid.nullish().transform((v) => v ?? null),
  name: z.string().trim().min(1).max(200),
  description: notes,
  justification: z.string().trim().min(3).max(1000),
});
export type FpaSpendItemRequestInput = z.infer<typeof fpaSpendItemRequestInput>;

/** Resolver um pedido: aprovar com o item que atende (novo ou existente) ou recusar com motivo. */
export const fpaSpendItemResolveInput = z
  .object({
    decision: z.enum(['approved', 'rejected']),
    itemId: uuid.nullish().transform((v) => v ?? null),
    note: z
      .string()
      .trim()
      .max(1000)
      .nullish()
      .transform((v) => (v ? v : null)),
  })
  .superRefine((v, ctx) => {
    if (v.decision === 'approved' && !v.itemId) {
      ctx.addIssue({
        code: 'custom',
        path: ['itemId'],
        message: 'Escolha o item que atende o pedido',
      });
    }
    if (v.decision === 'rejected' && (!v.note || v.note.length < 3)) {
      ctx.addIssue({ code: 'custom', path: ['note'], message: 'Registre o motivo da recusa' });
    }
  });

export const FPA_ACCOUNT_SCOPES = ['line', 'outside', 'out_of_scope'] as const;
export type FpaAccountScope = (typeof FPA_ACCOUNT_SCOPES)[number];

const accountMapFields = {
  scope: z.enum(FPA_ACCOUNT_SCOPES),
  plLineId: uuid.nullish().transform((v) => v ?? null),
  notes,
};
const lineWhenScoped = (
  v: { scope: FpaAccountScope; plLineId: string | null },
  ctx: z.RefinementCtx,
) => {
  if ((v.scope === 'line') !== !!v.plLineId) {
    ctx.addIssue({
      code: 'custom',
      path: ['plLineId'],
      message:
        v.scope === 'line'
          ? 'Escolha a linha gerencial'
          : 'Conta fora do gerencial ou do escopo não leva linha',
    });
  }
};
export const fpaAccountMapInput = z
  .object({ mappingVersionId: uuid, accountingAccountId: uuid, ...accountMapFields })
  .superRefine(lineWhenScoped);
export const fpaAccountMapUpdateInput = z.object(accountMapFields).superRefine(lineWhenScoped);

/** Conferência técnica do De-Para: Confirmado ou Corrigir (com o que corrigir). */
export const fpaAccountReviewInput = z
  .object({
    status: z.enum(['confirmed', 'correct', 'pending']),
    note: z
      .string()
      .trim()
      .max(1000)
      .nullish()
      .transform((v) => (v ? v : null)),
  })
  .superRefine((v, ctx) => {
    if (v.status === 'correct' && (!v.note || v.note.length < 3)) {
      ctx.addIssue({ code: 'custom', path: ['note'], message: 'Diga o que corrigir' });
    }
  });
